import { Dispatch, MutableRefObject, SetStateAction, useCallback } from "react";
import { MARKET_CONTRACT_SPECS_ABI } from "../../contracts/useMarketContractSpecs";
import { logger } from "../../../utils/logger";
import {
  parseRelayerResponse,
  parseRelayerResponseSoft,
} from "./relayerResponse";
import { ethers, BigNumber } from "ethers";
import {
  getWeb3ProviderForChain,
  readProvider,
} from "../../../utils/wallet/activeSigner";
import { SideResponse } from "../../../codegen-api";
import { getCurrentChainConfig } from "../../../config/chains";
import { toTokenUnits } from "../../../utils/decimalString";
import { getGaslessPerpRouter, getPerpDomainName } from "../../../utils/gaslessDomains";
import { resolveGaslessPerpAgent } from "./usePerpOrder.gasless";
import { InviteStatusResponse } from "../invite/useInviteStatus";
import { IClosePerpPositionOptions } from "./usePerpOrder.types";
import {
  PERP_CLOSE_GAS_FLOOR,
  PERP_ENGINE_ABI,
  PERP_ORDER_HANDLER_ABI,
  formatGaslessRelayError,
  resolvePerpMarginMode,
  toPerpTransactionError,
} from "./usePerpOrder.helpers";
import { orderRejected } from "./orderTransactionError";
import { useCancelConflictingCloseOrders } from "./usePerpOrder.cancelHelpers";
import { marketAcceptablePrice } from "./usePerpOrder.bounds";
import { PositionWithIdentity } from "../../../utils/positionIdentity";
import { TradeExecutedDetail } from "../../../utils/tradeEvents";

interface UseClosePerpPositionArgs {
  account: string | null | undefined;
  masterAccount: string | null | undefined;
  inviteStatus: InviteStatusResponse | undefined;
  /** The trader's max-slippage setting, basis points. */
  slippageBps: number;
  walletTransactionInFlightRef: MutableRefObject<boolean>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  revalidatePerpDataSoon: (detail?: TradeExecutedDetail) => void;
  cancelConflictingCloseOrders: ReturnType<typeof useCancelConflictingCloseOrders>;
}

export const useClosePerpPosition = ({
  account,
  masterAccount,
  inviteStatus,
  slippageBps,
  walletTransactionInFlightRef,
  setIsLoading,
  revalidatePerpDataSoon,
  cancelConflictingCloseOrders,
}: UseClosePerpPositionArgs) =>
  useCallback(
    async (
      position: PositionWithIdentity,
      options?: IClosePerpPositionOptions
    ): Promise<{ order_id: string; tx_hash: string }> => {
      if (!account) throw new Error("Not connected");
      if (!inviteStatus) {
        throw new Error("Checking invite access. Please try again in a moment.");
      }
      if (inviteStatus?.invite_required && !inviteStatus.allowed) {
        throw new Error("Invite code required to trade");
      }
      if (walletTransactionInFlightRef.current) {
        throw new Error(
          "Transaction already in progress. Please confirm or reject the open wallet request."
        );
      }

      const config = getCurrentChainConfig();
      const perpEngine = config.contracts.perpetualDex?.perpRouter || config.contracts.perpetualDex?.orderHandler;
      const gaslessPerpRouter = getGaslessPerpRouter(config);
      const perpDomainName = getPerpDomainName(config);
      const orderHandler = config.contracts.perpetualDex?.orderHandler;
      if (!perpEngine) throw new Error("PerpEngine not configured");

      walletTransactionInFlightRef.current = true;
      setIsLoading(true);
      try {
        // Chain-guarded: never sign a close for a network the wallet is not on.
        const provider = await getWeb3ProviderForChain(config);
        const signer = provider.getSigner();
        const perpContract = new ethers.Contract(perpEngine, PERP_ENGINE_ABI, signer);
        const orderHandlerContract = orderHandler
          ? new ethers.Contract(orderHandler, PERP_ORDER_HANDLER_ABI, signer)
          : undefined;

        const isLong = position.side === SideResponse.Buy;
        // `instrument_id` is the market address the router acts on. It is
        // optional on the position type, and submitting an undefined market
        // would target the zero address, so stop rather than guess.
        const market = position.instrument_id;
        if (!market) {
          throw new Error(
            `Could not determine the market for ${
              position.instrument_name || "this position"
            }. Refresh your positions and try again.`
          );
        }
        // Refuse to guess the margin mode. `margin_type` is optional on the
        // API payload, and this used to fall through to CROSS whenever it was
        // missing — submitting a cross-margin close for what may have been an
        // isolated position. There is no safe default here: the mode selects
        // which leg the router closes, so an unknown mode must stop the trade.
        const marginMode = resolvePerpMarginMode(position);

        // `toTokenUnits`, not `parseUnits`: exponent text ("1e-7") or more than
        // 18 decimals made ethers throw before the trader saw a real message.
        const requestedSize = options?.size
          ? toTokenUnits(options.size, 18)
          : ethers.BigNumber.from(0);
        if (!requestedSize) {
          throw new Error(`Invalid close size: ${options?.size}`);
        }
        let closeSize = requestedSize;

        // The market grid: `amountStep` floors a PARTIAL close so it can't
        // revert SizeNotOnStep after the user signs (mirrors placePerpOrder),
        // and `priceStep` keeps the price bound on the grid. RPC failures fall
        // through: the raw size (the contract still reverts clearly if it is
        // misaligned) and an unsnapped bound (still a valid bound).
        let amountStep: BigNumber | undefined;
        let priceStep: BigNumber | undefined;
        const dataStoreAddress = config.contracts.perpetualDex?.dataStore;
        if (dataStoreAddress && dataStoreAddress !== ethers.constants.AddressZero) {
          try {
            // Configured RPC, not the wallet: this is a view call, so it needs
            // a node on the right chain and nothing more. On the place path the
            // same read through the wallet failed whenever the wallet was on
            // another network, which surfaced as "Cannot read market lot size".
            const dataStore = new ethers.Contract(
              dataStoreAddress,
              MARKET_CONTRACT_SPECS_ABI,
              readProvider(config)
            );
            const specs = await dataStore.getMarketContractSpecs(market);
            priceStep = specs.priceStep ?? specs[0];
            amountStep = specs.amountStep ?? specs[1];
          } catch (e: any) {
            console.warn(
              "[PerpOrder] market specs read failed for close; submitting raw size:",
              e?.message
            );
          }
        }

        // A close is a reduce-only market order against the CLOB — closing a
        // long SELLS into the bids, closing a short BUYS the asks — so it is
        // bounded by the same max slippage as any market order. It used to
        // send 0 / MaxUint256: "close me at any price". A full close (size 0)
        // is bounded too; the bound is per fill, not per size.
        const acceptablePrice = await marketAcceptablePrice({
          config,
          market,
          isBuy: !isLong,
          slippageBps: options?.slippageBps ?? slippageBps,
          priceStep,
        });

        if (!closeSize.isZero()) {
          if (amountStep && amountStep.gt(0)) {
            const floored = closeSize.div(amountStep).mul(amountStep);
            if (floored.isZero()) {
              throw new Error(
                `Close size ${options?.size} is below the market's lot size. Increase the size.`
              );
            }
            closeSize = floored;
          }
        }

        // A zero size asks the router to close the whole position. This is the
        // trader's INTENT, not a guarantee of the outcome: the router turns it
        // into a reduce-only market order against the CLOB (see the gas comment
        // below), so a thin book can leave a remainder. Treat it as "a full
        // close was requested" and let the server confirm what actually
        // happened — never as proof the position is gone.
        const closedInFull = closeSize.isZero();

        logger.debug("[PerpOrder] Closing position...", {
          market,
          isLong,
          marginMode: marginMode === 0 ? "ISOLATED" : "CROSS",
          size: closedInFull ? "FULL" : ethers.utils.formatUnits(closeSize, 18),
        });

        // ── Gasless close: sign EIP-712 ClosePerpPosition + relay (user pays NO gas) ──
        // One-click signs with the stored agent key (no popup); otherwise the connected wallet.
        // The engine authorizes the signer via _isAuthorizedFor and settles to the position owner.
        const usdcAddress =
          config.contracts.perpetualDex?.usdc || config.contracts.erc20?.usdc;
        if (config.gasless && config.relayerUrl && gaslessPerpRouter && usdcAddress) {
          const relayer = config.relayerUrl.replace(/\/+$/, "");
          const nonceRes = await fetch(`${relayer}/nonce/${account}?router=perp`);
          const nonce: string = String((await parseRelayerResponse(nonceRes, "nonce")).nonce);
          const deadline = String(Math.floor(Date.now() / 1000) + 3600);
          const domain = {
            name: perpDomainName,
            version: "1",
            chainId: config.chainId,
            verifyingContract: gaslessPerpRouter,
          };
          const types = {
            ClosePerpPosition: [
              { name: "trader", type: "address" },
              { name: "market", type: "address" },
              { name: "collateralToken", type: "address" },
              { name: "isLong", type: "bool" },
              { name: "marginMode", type: "uint8" },
              { name: "sizeToClose", type: "uint256" },
              { name: "acceptablePrice", type: "uint256" },
              { name: "nonce", type: "uint256" },
              { name: "deadline", type: "uint256" },
            ],
          };
          const message = {
            trader: account,
            market,
            collateralToken: usdcAddress,
            isLong,
            marginMode,
            sizeToClose: closeSize,
            acceptablePrice,
            nonce,
            deadline,
          };
          // Sign with the MASTER's agent — when `account` is a subaccount, the order's trader is the
          // subaccount but the on-chain PerpEngine authorizes the master (or master's agent).
          // Resolve (and heal) via the gasless router so a stored-but-unapproved agent is
          // re-approved once instead of failing the close at the relay.
          const agentWallet = await resolveGaslessPerpAgent({
            account,
            masterAccount,
            chainId: config.chainId,
            perpEngine,
            gaslessPerpRouter,
            agentRegistry: config.contracts.perpetualDex?.agentRegistry,
            perpDomainName,
            relayer,
            signer,
          });
          const signature = agentWallet
            ? await agentWallet._signTypedData(domain, types, message)
            : await signer._signTypedData(domain, types, message);
          const relayRes = await fetch(`${relayer}/relay`, {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              action: "closePerpPosition",
              params: {
                trader: account,
                market,
                collateralToken: usdcAddress,
                sizeToClose: closeSize.toString(),
                isLong,
                marginMode,
                acceptablePrice: acceptablePrice.toString(),
                nonce,
                deadline,
              },
              signature,
            }),
          });
          const relayJson: any = await parseRelayerResponseSoft(relayRes, "order relay");
          if (!relayRes.ok || relayJson?.success === false || !relayJson?.txHash) {
            // Decoded like the gasless open: a close refused for slippage must
            // say so, not show the relayer's raw revert text.
            throw orderRejected(
              formatGaslessRelayError(
                relayJson?.error,
                "Gasless close failed. Please try again.",
                relayJson?.reverted
              )
            );
          }
          // Gasless: the relayer returned a hash but nothing waited for the
          // chain, so this is a broadcast acknowledgement, not a confirmation.
          revalidatePerpDataSoon({
            closedPosition: closedInFull ? position : undefined,
            closeProof: "broadcast",
          });
          return {
            order_id: relayJson.orderId?.toString() || relayJson.txHash,
            tx_hash: relayJson.txHash,
          };
        }

        if (orderHandlerContract) {
          await cancelConflictingCloseOrders(
            position,
            market,
            isLong,
            orderHandlerContract
          );
        }

        // A close is a reduce-only market order against the CLOB; matching
        // across several maker levels (plus PnL/fee/OI settlement) can exceed a
        // flat 2M. Estimate + 50% buffer with a Monad-safe fallback.
        const closeArgs = [
          account,
          market,
          isLong,
          marginMode,
          closeSize,         // sizeInTokensToClose = 0 → close entire position
          acceptablePrice,
        ] as const;
        let closeGasLimit = PERP_CLOSE_GAS_FLOOR;
        try {
          const estimatedGas = await perpContract.estimateGas.closePosition(
            ...closeArgs
          );
          const buffered = estimatedGas.mul(150).div(100);
          if (buffered.gt(closeGasLimit)) closeGasLimit = buffered;
        } catch {
          // estimateGas can fail on some wallet RPCs; keep the Monad-safe floor.
        }
        const tx = await perpContract.closePosition(...closeArgs, {
          gasLimit: closeGasLimit,
        });
        const receipt = await tx.wait(1);
        const orderId = receipt.events?.[0]?.args?.orderId?.toString() || receipt.transactionHash;

        logger.debug(`[PerpOrder] Position closed: orderId=${orderId}, tx=${tx.hash}`);
        revalidatePerpDataSoon({
          closedPosition: closedInFull ? position : undefined,
          closeProof: "receipt",
        });
        return { order_id: orderId, tx_hash: tx.hash };
      } catch (error: any) {
        throw toPerpTransactionError(error, "Failed to close perp position");
      } finally {
        walletTransactionInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [
      account,
      masterAccount,
      cancelConflictingCloseOrders,
      inviteStatus,
      revalidatePerpDataSoon,
      slippageBps,
    ]
  );
